import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  User,
  MapPin,
  Building2,
  Phone,
  AlertTriangle,
  ClipboardList,
  Share2,
  HeartPulse,
  QrCode,
  Copy,
  Check,
  Lock,
  LayoutDashboard,
  BookOpen,
  Package,
  Stethoscope,
  Pill,
  Droplets,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import BottomSheet from "../ui/bottom-sheet";
import { buildSafetyChecklist, getPublicProfileUrl } from "../profile/profile-utils";
import DownloadStickerButton from "../profile/DownloadStickerButton";
import { cn } from "../../lib/utils";
import QrBottomNav from "./QrBottomNav";
import MyQrStickerSheet from "./MyQrStickerSheet";

const Toast = ({ message, visible }) => (
  <div className={cn("qr-toast", visible && "qr-toast--visible")} role="status" aria-live="polite">
    {message}
  </div>
);

const ProgressRing = ({ percent, size = 64, label }) => {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  const grade = percent >= 90 ? "Excellent" : percent >= 70 ? "Good" : "Needs work";

  return (
    <div className="qr-prep-ring">
      <div className="qr-ring" style={{ width: size, height: size }}>
        <svg width={size} height={size} aria-hidden>
          <circle className="qr-ring__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
          <circle
            className="qr-ring__fill qr-ring__fill--success"
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
          />
        </svg>
        <span className="qr-ring__label">{percent}%</span>
      </div>
      <div>
        <p className="qr-prep-ring__title">Preparedness</p>
        <p className="qr-prep-ring__grade">{label || grade}</p>
      </div>
    </div>
  );
};

const QrOwnerDashboard = ({
  qrId,
  isOwner,
  token,
  ownerName,
  ownerPhoto,
  publicInfo,
  onReportOpen,
  showReportDisclaimer,
  onReportClose,
  onReportConfirm,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [toast, setToast] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [showMyQrSheet, setShowMyQrSheet] = useState(false);

  const sections = useMemo(() => publicInfo?.sections || [], [publicInfo?.sections]);
  const completion = useMemo(
    () =>
      buildSafetyChecklist({
        profile: {
          name: ownerName,
          photo: ownerPhoto,
          phone: publicInfo?.customer?.phone,
        },
        sections,
        qrActive: true,
      }),
    [ownerName, ownerPhoto, publicInfo?.customer?.phone, sections]
  );

  const showToast = useCallback((msg) => {
    setToast(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2200);
  }, []);

  const copyQrId = async () => {
    try {
      await navigator.clipboard.writeText(qrId);
      showToast("QR ID copied");
    } catch (_) {}
  };

  const handleViewProfile = () => {
    setViewLoading(true);
    navigate(`/${qrId}/public`, { state: { skipScanAlert: true } });
  };

  const handleShare = async () => {
    const url = getPublicProfileUrl(qrId);
    if (navigator.share) {
      try {
        await navigator.share({ title: "Emergency QR", url });
        return;
      } catch (_) {}
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast("Link copied");
    } catch (_) {}
  };

  const meds = sections
    .flatMap((s) => s.items || [])
    .filter((i) => /medication|medicine|दवा/i.test(i.key) && i.value?.trim())
    .map((i) => i.value)
    .slice(0, 2);

  const doctors = sections
    .flatMap((s) => s.items || [])
    .filter((i) => /doctor|डॉक्टर|hospital/i.test(i.key) && i.value?.trim())
    .map((i) => i.value)
    .slice(0, 1);

  const pills = [
    { label: "Blood group", value: completion.blood?.value, icon: Droplets },
    { label: "Allergies", value: completion.allergies?.value, icon: AlertTriangle },
    { label: "Medicines", value: meds.join(", "), icon: Pill },
    { label: "Doctors", value: doctors.join(", "), icon: Stethoscope },
  ].filter((p) => p.value);

  const incomplete = completion.percent < 100;
  const canManage = isOwner;
  const showOwnerLogin = !canManage && !token;

  const quickActions = [
    {
      id: "view",
      icon: QrCode,
      label: "View QR",
      sub: "Open emergency profile",
      onClick: handleViewProfile,
    },
    canManage
      ? {
          id: "edit",
          icon: ClipboardList,
          label: t("landing.editInfo"),
          sub: "Update medical details",
          onClick: () => navigate(`/${qrId}/info`),
        }
      : showOwnerLogin
        ? {
            id: "login",
            icon: Lock,
            label: t("landing.ownerLogin"),
            sub: "Sign in to manage",
            onClick: () => navigate(`/${qrId}/edit`),
          }
        : null,
    {
      id: "report",
      icon: AlertTriangle,
      label: t("public.reportEmergency"),
      sub: "Notify contacts",
      onClick: onReportOpen,
      danger: true,
    },
    {
      id: "nearby",
      icon: MapPin,
      label: "Nearby hospitals",
      sub: "Hospitals & blood banks",
      onClick: () => navigate(`/${qrId}/nearby`),
    },
    ...(token
      ? [
          {
            id: "orders",
            icon: Package,
            label: "Track orders",
            sub: "Order history",
            onClick: () => navigate("/profile"),
          },
        ]
      : []),
    {
      id: "guide",
      icon: BookOpen,
      label: "Setup guide",
      sub: "How EmergencyQR works",
      onClick: () => navigate("/guide"),
    },
  ].filter(Boolean);

  const userCard = (
    <section className="qr-card qr-card--info">
      <div className="qr-user-card">
        <div className="qr-user-card__top">
          {ownerPhoto ? (
            <img src={ownerPhoto} alt="" className="qr-user-card__photo" />
          ) : (
            <div className="qr-user-card__photo qr-user-card__photo--placeholder">
              <User size={28} strokeWidth={1.5} />
            </div>
          )}
          <div className="qr-user-card__meta">
            <h2 className="qr-user-card__name">{ownerName || "QR holder"}</h2>
            <div className="qr-user-card__id-row">
              <span className="qr-user-card__id font-mono">{qrId}</span>
              <button type="button" className="qr-copy-btn" onClick={copyQrId} aria-label="Copy QR ID">
                <Copy size={14} />
                Copy
              </button>
            </div>
            <span className="qr-badge qr-badge--success">
              <span className="qr-badge__dot" aria-hidden />
              {t("landing.activeTitle")}
            </span>
          </div>
        </div>

        <div className="qr-user-card__progress">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="qr-card__label">Profile completeness</p>
            <span className="qr-user-card__percent">{completion.percent}%</span>
          </div>
          <div className="qr-progress-bar" role="progressbar" aria-valuenow={completion.percent} aria-valuemin={0} aria-valuemax={100}>
            <div className="qr-progress-bar__fill" style={{ width: `${completion.percent}%` }} />
          </div>
          {isOwner && incomplete && (
            <button type="button" className="qr-link-btn" onClick={() => navigate("/profile")}>
              Complete profile →
            </button>
          )}
        </div>
      </div>
    </section>
  );

  const preparednessCard = (
    <section className="qr-card qr-card--info">
      <ProgressRing percent={completion.percent} />
      <ul className="qr-checklist">
        {completion.items
          .filter((i) => ["blood", "contact", "allergies", "identification"].includes(i.id))
          .map((item) => (
            <li key={item.id} className={cn("qr-checklist__item", item.done && "qr-checklist__item--done")}>
              {item.done ? <Check size={14} /> : <span className="qr-checklist__empty">□</span>}
              {item.label}
            </li>
          ))}
      </ul>
      {completion.items.some((i) => i.id === "identification" && !i.done) && (
        <p className="qr-card__muted mt-3">Missing: Insurance / identification details</p>
      )}
    </section>
  );

  const qrCard = (
    <section className="qr-card qr-card--info">
      <p className="qr-card__eyebrow">My QR</p>
      <h3 className="qr-card__title">QR ID</h3>
      <p className="qr-qr-id">{qrId}</p>
      {publicInfo?.scanCount != null && (
        <p className="qr-card__muted mb-4">{publicInfo.scanCount} scans</p>
      )}
      <div className="qr-qr-actions">
        <button type="button" className="checkout-btn checkout-btn--secondary flex-1" onClick={() => navigate(`/${qrId}/public`, { state: { skipScanAlert: true } })}>
          View QR
        </button>
        <DownloadStickerButton className="flex-1" />
        <button type="button" className="checkout-btn checkout-btn--secondary flex-1" onClick={handleShare}>
          <Share2 size={16} />
          Share
        </button>
      </div>
    </section>
  );

  const heroCard = incomplete ? (
    <section className="qr-hero qr-hero--incomplete">
      <div className="qr-hero__content">
        <p className="qr-hero__eyebrow">Emergency QR</p>
        <h1 className="qr-hero__title">Complete your emergency profile</h1>
        <p className="qr-hero__percent">{completion.percent}% complete</p>
        <p className="qr-hero__desc">
          Finish setup so first responders have everything they need when your QR is scanned.
        </p>
        {canManage ? (
          <button type="button" className="qr-hero__cta" onClick={() => navigate("/profile")}>
            Complete profile
          </button>
        ) : showOwnerLogin ? (
          <button type="button" className="qr-hero__cta" onClick={() => navigate(`/${qrId}/edit`)}>
            Owner login
          </button>
        ) : null}
      </div>
      <Shield className="qr-hero__shield" size={120} strokeWidth={1} aria-hidden />
    </section>
  ) : (
    <section className="qr-hero">
      <div className="qr-hero__content">
        <p className="qr-hero__eyebrow">Emergency QR</p>
        <p className="qr-hero__status-label">Emergency status</p>
        <div className="qr-hero__status">
          <span className="qr-badge__dot qr-badge__dot--lg" aria-hidden />
          ACTIVE
        </div>
        <h1 className="qr-hero__title">Your emergency profile is ready</h1>
        <p className="qr-hero__desc">
          Anyone scanning your QR can instantly view your emergency information.
        </p>
        <button
          type="button"
          className={cn("qr-hero__cta", viewLoading && "qr-hero__cta--loading")}
          onClick={handleViewProfile}
          disabled={viewLoading}
        >
          {viewLoading ? "Opening…" : "View emergency profile"}
        </button>
      </div>
      <Shield className="qr-hero__shield" size={120} strokeWidth={1} aria-hidden />
    </section>
  );

  return (
    <>
      <div className="qr-dashboard">
        <div className="qr-dashboard__layout">
          <aside className="qr-dashboard__sidebar hidden lg:flex">
            {userCard}
            {preparednessCard}
            {qrCard}
          </aside>

          <div className="qr-dashboard__main">
            {heroCard}

            <div className="lg:hidden">{userCard}</div>

            <section className="qr-card">
              <p className="qr-card__eyebrow">Quick actions</p>
              <div className="qr-action-grid">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={cn("qr-action-tile", action.danger && "qr-action-tile--danger")}
                    onClick={action.onClick}
                  >
                    <span className="qr-action-tile__icon">
                      <action.icon size={22} strokeWidth={1.75} />
                    </span>
                    <span className="qr-action-tile__label">{action.label}</span>
                    <span className="qr-action-tile__sub">{action.sub}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="qr-card qr-card--alert">
              <div className="qr-emergency-block">
                <div>
                  <p className="qr-card__eyebrow qr-card__eyebrow--danger">Emergency</p>
                  <h3 className="qr-card__title">Need immediate help?</h3>
                  <p className="qr-card__muted">Call emergency services or notify your contacts.</p>
                </div>
                <HeartPulse size={28} className="text-destructive shrink-0 opacity-80" />
              </div>
              <a href="tel:108" className="qr-emergency-cta">
                <Phone size={20} />
                Call 108 — Ambulance
              </a>
              <div className="qr-emergency-secondary">
                <a href="tel:100" className="qr-emergency-secondary__btn">
                  <Phone size={16} />
                  Police
                </a>
                <button type="button" className="qr-emergency-secondary__btn" onClick={() => navigate(`/${qrId}/public`, { state: { skipScanAlert: true } })}>
                  Emergency contacts
                </button>
                <button type="button" className="qr-emergency-secondary__btn" onClick={onReportOpen}>
                  Notify family
                </button>
              </div>
            </section>

            <section className="qr-card qr-card--hover qr-nearby-card">
              <div className="qr-hospital-block">
                <div className="qr-hospital-block__icon">
                  <Building2 size={24} strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <h3 className="qr-card__title">Nearby hospitals</h3>
                  <p className="qr-card__muted mb-3">Find nearby emergency services on the map.</p>
                  <div className="qr-hospital-tags">
                    <span>Hospitals</span>
                    <span>Trauma centers</span>
                    <span>Blood banks</span>
                    <span>Pharmacies</span>
                  </div>
                </div>
              </div>
              <button type="button" className="checkout-btn checkout-btn--secondary w-full mt-4" onClick={() => navigate(`/${qrId}/nearby`)}>
                <MapPin size={16} />
                Open maps
              </button>

              {/* Mobile-only: horizontal mini-card scroller */}
              <h3 className="qr-card__title qr-nearby-heading">Nearby services</h3>
              <div className="qr-nearby-scroller">
                {[
                  { id: "hospitals", icon: Building2, label: "Hospitals" },
                  { id: "blood", icon: Droplets, label: "Blood banks" },
                  { id: "pharmacy", icon: Pill, label: "Pharmacy" },
                  { id: "maps", icon: MapPin, label: "Maps" },
                ].map((svc) => (
                  <button
                    key={svc.id}
                    type="button"
                    className="qr-nearby-tile"
                    onClick={() => navigate(`/${qrId}/nearby`)}
                  >
                    <span className="qr-nearby-tile__icon">
                      <svc.icon size={20} strokeWidth={1.75} />
                    </span>
                    <span className="qr-nearby-tile__label">{svc.label}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="qr-card qr-card--report qr-card--hover">
              <div className="qr-report-block">
                <span className="qr-report-block__icon">
                  <AlertTriangle size={22} />
                </span>
                <div>
                  <h3 className="qr-card__title">{t("public.reportEmergency")}</h3>
                  <p className="qr-card__muted">
                    Notify emergency contacts with your live location.
                  </p>
                </div>
              </div>
              <button type="button" className="checkout-btn checkout-btn--secondary w-full border-destructive/20 text-destructive hover:bg-destructive/5" onClick={onReportOpen}>
                Report emergency
              </button>
            </section>

            {(isOwner || pills.length > 0) && (
              <section className="qr-card qr-card--hover">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="qr-card__title">Medical information</h3>
                    <p className="qr-card__muted">Critical details shown when your QR is scanned.</p>
                  </div>
                  {isOwner && (
                    <button type="button" className="qr-pill-btn" onClick={() => navigate(`/${qrId}/info`)}>
                      Edit
                    </button>
                  )}
                </div>
                {pills.length > 0 ? (
                  <div className="qr-med-pills">
                    {pills.map((p) => (
                      <span key={p.label} className="qr-med-pill">
                        <p.icon size={12} />
                        {p.label}: {p.value}
                      </span>
                    ))}
                    {completion.parsed.primaryContact?.number && (
                      <span className="qr-med-pill">
                        <Phone size={12} />
                        Contact: {completion.parsed.primaryContact.number}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="qr-card__muted">No medical details added yet.</p>
                )}
                {isOwner && (
                  <button type="button" className="checkout-btn checkout-btn--primary w-full mt-4" onClick={() => navigate(`/${qrId}/info`)}>
                    {t("landing.editInfo")}
                  </button>
                )}
              </section>
            )}

            {/* Mobile-only: recent activity timeline */}
            <section className="qr-card qr-activity">
              <h3 className="qr-card__title mb-3">Recent activity</h3>
              {publicInfo?.scanCount ? (
                <div className="qr-activity__group">
                  <p className="qr-activity__day">Recent</p>
                  <div className="qr-activity__item">
                    <span className="qr-activity__dot"><QrCode size={14} /></span>
                    QR scanned {publicInfo.scanCount} {publicInfo.scanCount === 1 ? "time" : "times"}
                  </div>
                </div>
              ) : null}
              {publicInfo?.lastUpdated ? (
                <div className="qr-activity__group">
                  <p className="qr-activity__day">Profile</p>
                  <div className="qr-activity__item">
                    <span className="qr-activity__dot"><Check size={14} /></span>
                    Emergency info updated
                  </div>
                </div>
              ) : null}
              <div className="qr-activity__group">
                <p className="qr-activity__day">Status</p>
                <div className="qr-activity__item">
                  <span className="qr-activity__dot"><Check size={14} /></span>
                  Emergency QR active
                </div>
              </div>
            </section>

            <div className="lg:hidden space-y-4">
              {preparednessCard}
              {qrCard}
            </div>

            {isOwner && (
              <section className="qr-card qr-card--hover">
                <button type="button" className="qr-account-row" onClick={() => navigate("/profile")}>
                  <LayoutDashboard size={20} />
                  <span>{t("nav.profile")}</span>
                </button>
              </section>
            )}

            <footer className="qr-dashboard__footer">
              EmergencyQR.in · 112 India
            </footer>
          </div>
        </div>
      </div>

      {/* Mobile-only: sticky bottom CTA */}
      <div className="qr-bottom-cta">
        <button type="button" className="qr-bottom-cta__btn" onClick={handleViewProfile}>
          <QrCode size={18} />
          View QR
        </button>
        <button type="button" className="qr-bottom-cta__btn qr-bottom-cta__btn--danger" onClick={onReportOpen}>
          <AlertTriangle size={18} />
          Report
        </button>
      </div>

      {/* Mobile-only: native bottom navigation */}
      <QrBottomNav
        qrId={qrId}
        activeTab="home"
        isOwner={canManage}
        isAuthenticated={Boolean(token)}
        onEmergency={onReportOpen}
        onMyQr={canManage || token ? () => setShowMyQrSheet(true) : undefined}
      />
      {(canManage || token) && (
        <MyQrStickerSheet open={showMyQrSheet} onClose={() => setShowMyQrSheet(false)} qrId={qrId} />
      )}

      <Toast message={toast} visible={toastVisible} />

      <BottomSheet
        open={showReportDisclaimer}
        onClose={onReportClose}
        title={t("public.reportEmergency")}
        secondaryAction={{ label: t("shared.cancel"), onClick: onReportClose }}
        primaryAction={{ label: t("public.understandReport"), onClick: onReportConfirm }}
      >
        <p className="text-base text-slate-700 mb-3">{t("public.reportDisclaimerIntro")}</p>
        <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
          {t("public.govRecognitionDesc")}
        </p>
      </BottomSheet>
    </>
  );
};

export default QrOwnerDashboard;
